<template>
  <div class="altcha-wrapper" ref="altchaContainer"></div>
</template>

<script>
export default {
  name: 'AltchaWidget',
  props: {
    challengeUrl: {
      type: String,
      default: 'https://altcha.bravenet.com/challenge'
    }
  },
  data() {
    return {
      value: null // This will hold the ALTCHA token
    };
  },
  computed: {
    // Expose value as a computed property for easier access
    token() {
      return this.value;
    }
  },
  created() {
    // Use non-reactive properties to prevent Vue from tracking them
    this._widgetSetup = false;
    this._initializing = false;
    this._setupRetries = 0;
    this._widgetElement = null;
  },
  mounted() {
    // Prevent duplicate initialization using non-reactive flag
    if (this._initializing) {
      return;
    }
    this._initializing = true;
    
    // Create the custom element programmatically to avoid Vue processing it
    const container = this.$refs.altchaContainer;
    if (!container) {
      console.error('ALTCHA: Container element not found');
      return;
    }
    
    // Create the altcha-widget element
    const widget = document.createElement('altcha-widget');
    widget.setAttribute('name', 'altcha');
    widget.setAttribute('challengeurl', this.challengeUrl);
    container.appendChild(widget);
    this._widgetElement = widget;
    
    // Initialize after a short delay to ensure the element is in the DOM
    setTimeout(() => {
      this.initializeAltcha();
    }, 50);
  },
  watch: {
    challengeUrl(newVal) {
      // Update attribute when prop changes
      if (this._widgetElement) {
        this._widgetElement.setAttribute('challengeurl', newVal);
      }
    }
  },
  beforeDestroy() {
    // Clean up event listeners and remove the widget
    if (this._widgetElement) {
      if (this._widgetElement._altchaVerifiedHandler) {
        this._widgetElement.removeEventListener('verified', this._widgetElement._altchaVerifiedHandler);
      }
      if (this._widgetElement._altchaStateChangeHandler) {
        this._widgetElement.removeEventListener('statechange', this._widgetElement._altchaStateChangeHandler);
      }
      // Remove the widget from DOM
      if (this._widgetElement.parentNode) {
        this._widgetElement.parentNode.removeChild(this._widgetElement);
      }
      this._widgetElement = null;
    }
  },
  methods: {
    initializeAltcha() {
      // Prevent duplicate initialization
      if (this._initializing && this._widgetSetup) {
        return;
      }
      
      // Wait for ALTCHA widget to be defined
      if (typeof customElements === 'undefined') {
        console.error('ALTCHA: Custom elements not supported');
        this.$emit('error', 'custom-elements-not-supported');
        return;
      }
      
      if (customElements.get('altcha-widget')) {
        this.setupWidget();
      } else {
        // Wait for the ALTCHA widget to be defined
        customElements.whenDefined('altcha-widget')
          .then(() => {
            if (!this._widgetSetup) {
              this.setupWidget();
            }
          })
          .catch((error) => {
            console.error('ALTCHA widget failed to load:', error);
            this.$emit('error', 'api-not-loaded');
          });
      }
    },
    setupWidget() {
      // Prevent duplicate setup using non-reactive flag
      if (this._widgetSetup) {
        return;
      }
      
      const widget = this._widgetElement;
      if (!widget) {
        // Widget not ready yet, try again after a short delay (max 5 retries)
        if (this._setupRetries < 5) {
          this._setupRetries++;
          const self = this;
          setTimeout(() => {
            // Check if component is still mounted and widget exists
            if (self.$el && self.$el.parentNode && !self._widgetSetup && self._widgetElement) {
              self.setupWidget();
            }
          }, 200);
        } else {
          console.error('ALTCHA: Widget element not found after multiple retries');
          if (!this._widgetSetup) {
            this.$emit('error', 'element-not-found');
          }
        }
        return;
      }
      
      // Mark as setup immediately to prevent recursion
      this._widgetSetup = true;

      // Remove existing listeners to prevent duplicates
      if (widget._altchaVerifiedHandler) {
        widget.removeEventListener('verified', widget._altchaVerifiedHandler);
      }
      if (widget._altchaStateChangeHandler) {
        widget.removeEventListener('statechange', widget._altchaStateChangeHandler);
      }

      const verifiedHandler = (ev) => {
        console.log('ALTCHA: verified event received', ev.detail);
        if (ev.detail && ev.detail.payload) {
          this.value = ev.detail.payload;
          console.log('ALTCHA: Token captured:', this.value ? 'Token present' : 'Token missing');
          this.$emit('verify', this.value);
        } else {
          console.warn('ALTCHA: verified event missing payload', ev.detail);
        }
      };

      const stateChangeHandler = (ev) => {
        console.log('ALTCHA: statechange event received', ev.detail);
        if (ev.detail) {
          if (ev.detail.state === 'verified' && ev.detail.payload) {
            this.value = ev.detail.payload;
            console.log('ALTCHA: Token captured from statechange:', this.value ? 'Token present' : 'Token missing');
            this.$emit('verify', this.value);
          } else if (ev.detail.state === 'error') {
            console.error('ALTCHA: Widget error state', ev.detail);
            this.value = null;
            this.$emit('error', 'verification-failed');
          } else if (ev.detail.state === 'expired') {
            console.warn('ALTCHA: Token expired');
            this.value = null;
            this.$emit('expired');
          } else {
            console.log('ALTCHA: Widget state:', ev.detail.state);
          }
        }
      };

      // Store handlers for cleanup
      widget._altchaVerifiedHandler = verifiedHandler;
      widget._altchaStateChangeHandler = stateChangeHandler;

      // Listen for ALTCHA verification
      widget.addEventListener('verified', verifiedHandler);

      // Listen for state changes
      widget.addEventListener('statechange', stateChangeHandler);
    },
    resetRecaptcha() {
      // Reset ALTCHA widget
      this.value = null;
      this._widgetSetup = false; // Allow re-setup
      const widget = this._widgetElement;
      if (widget && typeof widget.reset === 'function') {
        widget.reset();
      } else if (widget) {
        // Clean up old listeners
        if (widget._altchaVerifiedHandler) {
          widget.removeEventListener('verified', widget._altchaVerifiedHandler);
        }
        if (widget._altchaStateChangeHandler) {
          widget.removeEventListener('statechange', widget._altchaStateChangeHandler);
        }
        // Fallback: remove and recreate the widget
        const container = this.$refs.altchaContainer;
        if (container && widget.parentNode) {
          widget.parentNode.removeChild(widget);
          const newWidget = document.createElement('altcha-widget');
          newWidget.setAttribute('challengeurl', this.challengeUrl);
          newWidget.setAttribute('name', 'altcha');
          container.appendChild(newWidget);
          this._widgetElement = newWidget;
          setTimeout(() => {
            this.setupWidget();
          }, 50);
        }
      }
    }
  }
};
</script>

<style scoped>
.altcha-wrapper {
  display: inline-block;
  width: 100%;
  text-align: center;
}
</style>




// WEBPACK FOOTER //
// lib/brave-authentication/components/altcha.vue